import React, { FC } from "react";
import { Box, Button, Tooltip, Typography } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AddIcon from "@mui/icons-material/Add";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { StepDots } from "../components/StepDots";
import { ProjectSummaryRow } from "../components/ProjectSummaryRow";
import { OrderProjectValues, PreviewProjectEstimate } from "../helper";
import { getMilestones, addDays } from "../../../components/ProjectTimeline";

interface ProjectAddedStepProps {
    orderName: string;
    projects: OrderProjectValues[];
    justAddedIndex: number;
    contentTypes: { id: number; name: string }[];
    isEditing: boolean;
    onEdit: (index: number) => void;
    onRemove: (index: number) => void;
    onAddAnother: () => void;
    onReview: () => void;
    // Estimated per-project prices keyed by project index (from live preview).
    pricingByKey?: Map<number, PreviewProjectEstimate> | null;
    brandId?: number | string | null;
    // The order's brand (RMN pricing hide on the per-project price).
    brand?: { rmn?: boolean } | null;
}

export const ProjectAddedStep: FC<ProjectAddedStepProps> = ({
    orderName,
    projects,
    justAddedIndex,
    contentTypes,
    isEditing,
    onEdit,
    onRemove,
    onAddAnother,
    onReview,
    pricingByKey,
    brandId,
    brand,
}) => {
    const total = projects.length;
    const priceFor = (index: number): number | null =>
        pricingByKey?.get(index)?.total ?? null;
    const breakdownFor = (index: number) =>
        pricingByKey?.get(index)?.breakdown ?? null;
    const deliveryOffset = getMilestones(1).at(-1)!.daysOffset;
    const projectDates = projects.map((p) => (p.start_date instanceof Date ? p.start_date : new Date()));
    const earliestStart = new Date(Math.min(...projectDates.map((d) => d.getTime())));
    const latestDelivery = new Date(Math.max(...projectDates.map((d) => addDays(d, deliveryOffset).getTime())));
    const estimatedDays = Math.round((latestDelivery.getTime() - earliestStart.getTime()) / 86400000);

    return (
        <Box sx={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column", p: "28px 36px 24px" }}>
            {/* Header */}
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb="14px">
                <Box display="flex" alignItems="center" gap="12px">
                    <CheckCircleIcon sx={{ fontSize: 32, color: "var(--accent)", flexShrink: 0 }} />
                    <Box>
                        <Typography
                            sx={{
                                fontSize: 22,
                                fontWeight: 700,
                                color: "var(--text)",
                                letterSpacing: -0.4,
                                fontFamily: "CircularStd",
                            }}
                        >
                            {isEditing ? "Project Updated!" : "Project Added!"}
                        </Typography>
                        <Typography sx={{ fontSize: 13, color: "var(--text-2)", mt: "2px", fontFamily: "CircularStd" }}>
                            {isEditing ? (
                                "Your changes have been saved to this order."
                            ) : (
                                <>
                                    You've added {total} project{total !== 1 ? "s" : ""} to{" "}
                                    <Box component="strong" sx={{ color: "var(--text)" }}>
                                        {orderName}
                                    </Box>
                                    .
                                </>
                            )}
                        </Typography>
                    </Box>
                </Box>
                <StepDots step={5} />
            </Box>

            {/* Scrollable project list */}
            <Box sx={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '8px', minHeight: 0 }}>
                {/* Just-added/updated project — highlighted */}
                {projects[justAddedIndex] && (
                    <ProjectSummaryRow
                        project={projects[justAddedIndex]}
                        brandId={brandId}
                        brand={brand}
                        fresh
                        freshLabel={isEditing ? 'Just updated' : 'Just added'}
                        contentTypes={contentTypes}
                        projectPrice={priceFor(justAddedIndex)}
                        priceBreakdown={breakdownFor(justAddedIndex)}
                        onEdit={() => onEdit(justAddedIndex)}
                        onRemove={() => onRemove(justAddedIndex)}
                    />
                )}

                {/* All other projects */}
                {total > 1 && (
                    <>
                        <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            sx={{ mt: "4px" }}
                        >
                            <Typography
                                sx={{
                                    fontSize: 10.5,
                                    fontWeight: 700,
                                    color: "var(--text-2)",
                                    letterSpacing: 0.6,
                                    textTransform: "uppercase",
                                    fontFamily: "CircularStd",
                                }}
                            >
                                All projects in this order · {total}
                            </Typography>
                            <Tooltip
                                title="Estimated based on average time per project type. Final scope confirmed after kickoff."
                                placement="top"
                            >
                                <Typography
                                    sx={{
                                        fontSize: 11,
                                        color: "var(--text-2)",
                                        fontFamily: "CircularStd",
                                        cursor: "default",
                                        textDecoration: "underline dotted",
                                        textDecorationColor: "var(--muted)",
                                    }}
                                >
                                    Est. ~{estimatedDays} days to delivery
                                </Typography>
                            </Tooltip>
                        </Box>
                        {projects.map((p, i) => {
                            if (i === justAddedIndex) return null;
                            return (
                                <ProjectSummaryRow
                                    key={p.project_id ?? i}
                                    project={p}
                                    brandId={brandId}
                                    brand={brand}
                                    contentTypes={contentTypes}
                                    projectPrice={priceFor(i)}
                                    priceBreakdown={breakdownFor(i)}
                                    onEdit={() => onEdit(i)}
                                    onRemove={() => onRemove(i)}
                                />
                            );
                        })}
                    </>
                )}
            </Box>

            {/* Action buttons */}
            <Box
                sx={{
                    mt: "auto",
                    pt: "16px",
                    display: "flex",
                    gap: "12px",
                    flexShrink: 0,
                }}
            >
                <Button
                    variant="outlined"
                    color="primary"
                    fullWidth
                    startIcon={<AddIcon />}
                    onClick={onAddAnother}
                    sx={{
                        textTransform: "capitalize",
                        fontFamily: "CircularStd",
                        borderRadius: "24px",
                        fontWeight: 600,
                    }}
                >
                    Add Another Project
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    endIcon={<ArrowForwardIcon />}
                    onClick={onReview}
                    sx={{
                        textTransform: "capitalize",
                        fontFamily: "CircularStd",
                        borderRadius: "24px",
                        fontWeight: 600,
                    }}
                >
                    Review Order
                </Button>
            </Box>
        </Box>
    );
};
